import { useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useVendorAuth } from '../../context/VendorAuthContext';
import {
  LayoutDashboard,
  Package,
  ShoppingCart,
  Users,
  MapPin,
  LogOut,
  Menu,
  X,
  User,
} from 'lucide-react';
import { Button } from '../../components/ui/button';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_neighbor-market-6/artifacts/78ujw3e9_LocaLS_Logo.png";

const VendorLayout = () => {
  const { vendor, logout } = useVendorAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const handleLogout = async () => {
    await logout();
    navigate('/vendor/login');
  };

  const navItems = [
    { name: 'Dashboard', path: '/vendor/dashboard', icon: LayoutDashboard },
    { name: 'Products', path: '/vendor/products', icon: Package },
    { name: 'Orders', path: '/vendor/orders', icon: ShoppingCart },
    { name: 'Customers', path: '/vendor/customers', icon: Users },
    { name: 'Pickup Points', path: '/vendor/pickup-points', icon: MapPin },
    { name: 'Profile', path: '/vendor/profile', icon: User },
  ];

  return (
    <div className="min-h-screen bg-background-secondary flex">
      {/* Sidebar */}
      <aside
        className={`${
          sidebarOpen ? 'w-64' : 'w-20'
        } bg-white border-r border-surface-border transition-all duration-300 flex flex-col`}
      >
        {/* Logo */}
        <div className="p-4 border-b border-surface-border">
          <div className="flex items-center gap-3">
            <div className="bg-white rounded-xl p-2 border border-surface-border">
              <img
                src={LOGO_URL}
                alt="LocaLS"
                className="h-8 w-auto object-contain"
              />
            </div>
            {sidebarOpen && (
              <span className="font-heading font-bold text-xl text-text-primary">Vendor</span>
            )}
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-2">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-xl transition-colors font-body font-medium ${
                  isActive
                    ? 'bg-brand-primary text-white font-semibold'
                    : 'text-text-secondary hover:bg-background-secondary hover:text-text-primary'
                }`
              }
              data-testid={`vendor-nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <item.icon size={20} />
              {sidebarOpen && <span>{item.name}</span>}
            </NavLink>
          ))}
        </nav>

        {/* User & Logout */}
        <div className="p-4 border-t border-surface-border">
          {sidebarOpen && (
            <div className="mb-4 px-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full overflow-hidden bg-brand-primary/10 flex items-center justify-center shrink-0 ring-1 ring-surface-border">
                {vendor?.profile_image ? (
                  <img
                    src={vendor.profile_image}
                    alt={vendor?.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <span className="font-heading font-bold text-brand-primary text-sm">
                    {(vendor?.name || 'V')
                      .split(' ')
                      .map((w) => w[0])
                      .slice(0, 2)
                      .join('')
                      .toUpperCase()}
                  </span>
                )}
              </div>
              <div className="min-w-0">
                <p className="font-body font-semibold text-text-primary truncate text-sm">{vendor?.name}</p>
                <p className="font-body text-xs text-text-muted truncate">{vendor?.email}</p>
              </div>
            </div>
          )}
          <Button
            onClick={handleLogout}
            variant="outline"
            className="w-full justify-center gap-2 rounded-xl border-surface-border text-text-secondary hover:text-red-600 hover:border-red-200 hover:bg-red-50"
            data-testid="vendor-logout-btn"
          >
            <LogOut size={18} />
            {sidebarOpen && <span>Logout</span>}
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col">
        {/* Top Bar */}
        <header className="bg-white border-b border-surface-border px-6 py-4 flex items-center justify-between">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 rounded-lg hover:bg-background-secondary transition-colors"
          >
            {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
          <div className="flex items-center gap-4">
            <span className="font-body text-text-secondary">Welcome, {vendor?.name}</span>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 p-6 overflow-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default VendorLayout;
